// usePWAInstall — captures the browser's `beforeinstallprompt` event so the
// app can offer its own install button, and tracks whether the app is
// already running standalone (installed).
//
// Returns:
//   {
//     canInstall: boolean,   // true when an install prompt is queued
//     isInstalled: boolean,  // true when launched from the installed app
//     promptInstall: () => Promise<"accepted"|"dismissed"|"unavailable">,
//   }
//
// Notes:
//   • Chrome / Edge fire `beforeinstallprompt` once per page load, only when
//     PWA install criteria are met (manifest + service worker + https-or-
//     localhost).  We stash the event so we can call `.prompt()` later.
//   • iOS Safari does NOT fire this event — installation there is manual
//     ("Add to Home Screen").  The UI falls back to instructions in that
//     case via `isStandaloneCapable`.
//   • Once the user installs the app the browser fires `appinstalled`; we
//     clear the queued prompt and mark `isInstalled` true.

import { useEffect, useState, useCallback } from "react";

function detectStandalone() {
  if (typeof window === "undefined") return false;
  // Chrome / Android / desktop
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  if (window.navigator.standalone === true) return true;
  return false;
}

export function usePWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [isInstalled, setIsInstalled] = useState(detectStandalone());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();              // suppress the mini-infobar
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return "unavailable";
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);                 // a deferred prompt can only fire once
    return choice?.outcome || "dismissed";
  }, [deferred]);

  return {
    canInstall: !!deferred,
    isInstalled,
    promptInstall,
  };
}
